import { useState } from 'react';
import Select from 'react-select';
import { ReactSVG } from 'react-svg';
import Switch from 'react-switch';
import AddressField from '../AddressField';

import brandingIcon from '../../images/icons/screens/paintbrush.svg';
import busSideview from '../../images/icons/subscriptions/bus-sideview.svg';
import chartUpwardIcon from '../../images/icons/chart-upward.svg';
import compassIcon from '../../images/icons/menu/compass.svg';
import contentLibraryIcon from '../../images/icons/subscriptions/content-library.svg';
import contentManagementIcon from '../../images/icons/menu/content-management.svg';
import databaseIcon from '../../images/icons/subscriptions/database.svg';
import directoryIcon from '../../images/icons/screens/building.svg';
import easelGraphIcon from '../../images/icons/easel-graph.svg';
import insightIcon from '../../images/icons/menu/insight.svg';
import interactivityIcon from '../../images/icons/screens/interactivity.svg';
import foresightIcon from '../../images/icons/menu/foresight.svg';
import mobilityScoreIcon from '../../images/icons/menu/mobilityscore.svg';
import mobilityscoreProIcon from '../../images/icons/subscriptions/mobilityscorePro.svg';
import nearbyIcon from '../../images/icons/screens/coffee.svg';
import playlistsIcon from '../../images/icons/menu/playlists.svg';
import privateModeIcon from '../../images/icons/modes/shuttle.svg';
import screenAndMobileIcon from '../../images/icons/menu/screen.svg';
import transitIcon from '../../images/icons/screens/transit.svg';
import tsIcon from '../../images/icons/ts-icon.svg';
import wayfindingIcon from '../../images/icons/screens/paper-plane.svg';
import weatherIcon from '../../images/icons/screens/weather.svg';

import subscriptionsJson from './subscriptions.json';

import { formatAddress } from './utils';
import { isAfAdmin } from '../../utils';

const icons = {
    brandingIcon,
    busSideview,
    chartUpwardIcon,
    compassIcon,
    contentManagementIcon,
    contentLibraryIcon,
    databaseIcon,
    directoryIcon,
    easelGraphIcon,
    foresightIcon,
    insightIcon,
    interactivityIcon,
    mobilityScoreIcon,
    mobilityscoreProIcon,
    nearbyIcon,
    playlistsIcon,
    privateModeIcon,
    screenAndMobileIcon,
    transitIcon,
    tsIcon,
    wayfindingIcon,
    weatherIcon,
};

/* React-Select custom styling */
const basicConfigDropdownStyling = {
    container: (baseStyles, state) => ({
      ...baseStyles,
      // Too much gray if the dropdown is disabled.
      boxShadow: state.isDisabled ? "unset" : "0px 2px 8px 4px rgba(0, 0, 0, 0.09)",
      borderRadius: "8px"
    }),
    control: (baseStyles, state) => ({
        ...baseStyles,
        border: "none",
        cursor: "pointer"
    }),
    indicatorSeparator: (baseStyles, state) => ({
        ...baseStyles,
        display: "none"
    }),
    clearIndicator: (baseStyles, state) => ({
        ...baseStyles,
        display: "none"
    }),
    option: (baseStyles, state) => ({
        ...baseStyles,
        color: state.isDisabled ? "#919191" : "inherit",
        cursor: "pointer"
    })
};

let svgNeedsFill = [
    'brandingIcon',
    'busSideview',
    'chartUpwardIcon',
    'compassIcon',
    'contentLibraryIcon',
    'databaseIcon',
    'easelGraphIcon',
    'foresightIcon',
    'insightIcon',
    'interactivityIcon',
    'mobilityscoreProIcon',
    'nearbyIcon',
    'privateModeIcon',
    'screenAndMobileIcon',
    'weatherIcon',
    'tsIcon',
];
const fillSvgColor = (svg, stroke, fill) => {
    if (stroke || fill) {
        for (var i = 0; i < svg.children.length; i++) {
            svg.children[i].setAttribute('stroke', stroke);
            svg.children[i].setAttribute('fill', fill);
        }
    }
}

const Subscriptions = ({
    isCreatedLocation,
    location,
    setEditLocation,
    handleLocationChange,
    userRoles,
    setUserRoles,
	users,
    setIsLocationEdited,
    customer,
    userData
}) => {
    const userOptions = users.map(user => ({
        value: user.user_id,
        label: `${user.given_name} ${user.family_name}`
    }));

    const existingUserRoles = Object.fromEntries(
        Object.entries(userRoles).map(([subscription, roles]) => [
          subscription,
          Object.fromEntries(
            Object.entries(roles).map(([role, users]) => {      
              return [
                role,
                Array.isArray(users)
                  ? users.map(({ userId, name }) => ({
                      value: userId,
                      label: name
                    }))
                  : [] // fallback to empty array if users is not an array
              ];
            })
          )
        ])
    );

    const changeInput = (newList, actionMeta, role, subscriptionKey) => {
        let tempUserRoles = {
            ...userRoles[subscriptionKey]
        }
        const tempAllUserRoles = {
            ...userRoles
        }

        if (actionMeta.action === 'select-option') {
            if (!tempUserRoles[role] || tempUserRoles[role].length === 0) {
                tempUserRoles[role] = [{
                    locationId: location.id,
                    name: actionMeta.option.label,
                    role,
                    subscription: subscriptionKey,
                    userId: actionMeta.option.value
                }]
            }
            else {
                tempUserRoles[role].push({
                    locationId: location.id,
                    name: actionMeta.option.label,
                    role,
                    subscription: subscriptionKey,
                    userId: actionMeta.option.value
                });
            }
            tempAllUserRoles[subscriptionKey] = tempUserRoles;
        }
        else if (actionMeta.action === 'remove-value') {
            tempUserRoles[role] = tempUserRoles[role].filter(userRole => {
                return userRole.userId !== actionMeta.removedValue.value
            });
            tempAllUserRoles[subscriptionKey] = tempUserRoles;
        }
        setIsLocationEdited(true);
        setUserRoles(tempAllUserRoles);
    }

    const getEditorViewerSelect = (permission, subscriptionKey, sectionEnabled) => {
        // Which people
        let selectValues = [];
        if (existingUserRoles[subscriptionKey] && existingUserRoles[subscriptionKey][permission]) {
            selectValues = existingUserRoles[subscriptionKey][permission];
        }

        // Is the select-people box enabled?
        let selectDisabled = !sectionEnabled;

        return <div className="select-editor-viewer-container" style={permission === 'editor' ? {marginBottom: "0.5em"} : null}>
                <div className="label">{permission.charAt(0).toUpperCase() + permission.slice(1)}:</div>
                <div className="select-editor-viewer">
                    <Select
                        isMulti
                        isDisabled={selectDisabled}
                        options={userOptions}
                        placeholder={permission.charAt(0).toUpperCase() + permission.slice(1)}
                        onChange={handleSelectChange(permission, subscriptionKey)}
                        value={selectValues}
                        styles={basicConfigDropdownStyling}
                    />
                </div>
            </div>
    }

    const handleSelectChange = (role, productKey) => (newList, actionMeta) => {
        changeInput(newList, actionMeta, role, productKey);
    };

    const selectSubSubscription = (subscription, subSubscription, value) => {
        if  (!subscription.defaultSubscriptions || !(subSubscription in subscription?.defaultSubscriptions)) {
            const tempPurchasedProducts = {
                ...location.purchasedProducts
            }
            tempPurchasedProducts[subSubscription] = value

            handleLocationChange(
                'purchasedProducts',
                tempPurchasedProducts
            );
        }
    }

	return ( 
		<div className="Subscriptions">
            <div className="subscriptions-container">
                <div className="section-body">
                    {
                        location?.id || isCreatedLocation
                        ? <div className="left-column">
                            <h3>Subscriptions at this location</h3>
                            {
                                subscriptionsJson.mainSubscriptions.map((subscription, idx) => {
                                    const mainSubscription = subscriptionsJson.subscriptions[subscription]

                                    // Eg. GTFS Architect
                                    if (!('subSubscriptions' in mainSubscription)) {
                                        return <SubscriptionBlockWithoutSubSubscriptions
                                            subscriptionKey={subscription}
                                            subscription={mainSubscription}
                                            userOptions={userOptions}
                                            location={location}
                                            handleLocationChange={handleLocationChange}
                                            sectionEnabled={location?.purchasedProducts[subscription]}
                                            userRoles={userRoles}
                                            setUserRoles={setUserRoles}
                                            existingUserRoles={existingUserRoles || []}
                                            setIsLocationEdited={setIsLocationEdited}
                                            key={subscription}
                                            changeInput={changeInput}
                                            handleSelectChange={handleSelectChange}
                                            getEditorViewerSelect={getEditorViewerSelect}
                                            editableSubscriptions={isAfAdmin(userData)}
                                        />
                                    } else {
                                        let purchasedSubscriptions = Object.keys(mainSubscription.subSubscriptions).reduce((acc, key) => {
                                            if (key in location.purchasedProducts) {
                                                acc[key] = location.purchasedProducts[key];
                                            }
                                            return acc;
                                        }, {});
                                        purchasedSubscriptions = {...mainSubscription.defaultSubscriptions, ...purchasedSubscriptions};

                                        // Eg. Content Management section
                                        if (!('viewerSelect' in mainSubscription) && !('editorSelect' in mainSubscription)) {
                                            return <SubscriptionBlockWithGranularPermissions
                                                subscriptionKey={subscription}
                                                subscription={mainSubscription}
                                                userOptions={userOptions}
                                                location={location}
                                                handleLocationChange={handleLocationChange}
                                                sectionEnabled={location?.purchasedProducts[subscription]}
                                                purchasedSubscriptions={purchasedSubscriptions}
                                                userRoles={userRoles}
                                                setUserRoles={setUserRoles}
                                                existingUserRoles={existingUserRoles || []}
                                                setIsLocationEdited={setIsLocationEdited}
                                                key={subscription}
                                                handleSelectChange={handleSelectChange}
                                                editableSubscriptions={isAfAdmin(userData)}
                                            />
                                        } else {
                                            return <SubscriptionBlock
                                                subscriptionKey={subscription}
                                                subscription={mainSubscription}
                                                userOptions={userOptions}
                                                location={location}
                                                handleLocationChange={handleLocationChange}
                                                sectionEnabled={location?.purchasedProducts[subscription]}
                                                purchasedSubscriptions={purchasedSubscriptions}
                                                userRoles={userRoles}
                                                setUserRoles={setUserRoles}
                                                existingUserRoles={existingUserRoles || []}
                                                setIsLocationEdited={setIsLocationEdited}
                                                key={subscription}
                                                changeInput={changeInput}
                                                selectSubSubscription={selectSubSubscription}
                                                handleSelectChange={handleSelectChange}
                                                getEditorViewerSelect={getEditorViewerSelect}
                                                editableSubscriptions={isAfAdmin(userData)}
                                            />
                                        }
                                    }
                                })
                            }
                        </div>
                        : <div className="left-column">
                            <h3>Select a location</h3>
                        </div>
                    }
                    {
                        location?.id || isCreatedLocation
                        ? <div className="right-column">
                            <h3 className="title">Location details</h3>
                            <div className="section-block location-details">
                                <div>Changes made to these fields will impact all products linked to this location.</div>
                                <div className="input-container">
                                    <div className="label">Name:</div>
                                    <input 
                                        className={`input`} 
                                        onChange={(e) => handleLocationChange('name', e.target.value)} 
                                        value={location?.name || ''}
                                        disabled={!isAfAdmin(userData)}
                                    />
                                </div>
                                <div className="input-container">
                                    <div className="label">Address:</div>
                                    <AddressField 
                                        existingLocation={location}
                                        placeholder={'Search for an address'}
                                        editLocation={setEditLocation}
                                        searchValue={formatAddress(location)}
                                        setIsLocationEdited={setIsLocationEdited}
                                        disabled={!isAfAdmin(userData)}
                                    />
                                </div>
                            </div>
                        </div>
                        : <></>
                    }
                    
                </div>     
            </div>
		</div>
	);
}

const SubscriptionBlock = ({ subscriptionKey, subscription, userOptions, location, handleLocationChange, sectionEnabled, purchasedSubscriptions, userRoles, setUserRoles, existingUserRoles, setIsLocationEdited, changeInput, selectSubSubscription, handleSelectChange, getEditorViewerSelect, editableSubscriptions }) => {

    const getProductsList = (subscriptionKey) => {
        return <div className="products-list">
            <div className="content-left">
                {/* Content */}
                <div className={`section-content ${sectionEnabled ? '' : 'disabled'}`}>
                    {Object.keys(subscription.subSubscriptions).map((key, idx) => {
                        const subSubscription = subscription.subSubscriptions[key];
                        // If we happen to have a new subscription option and the key doesn't
                        // exist in the location "purchasedProducts" column, set to false
                        // to prevent ReactJS from complaining about turning uncontrolled
                        // inputs to controlled ones.
                        if (!(key in purchasedSubscriptions)) {
                            purchasedSubscriptions[key] = false;
                        }
                        return (
                            <div className="row" key={idx}>
                                <div className="icon-container">
                                    {
                                        subSubscription.icon in icons
                                        ? <ReactSVG
                                            className="icon"
                                            src={icons[subSubscription.icon]}
                                            beforeInjection={(svg) => fillSvgColor(
                                                svg,
                                                subSubscription.disabled ? '#97989b' : '#373739',
                                                (svgNeedsFill.includes(subSubscription.icon) ? (subSubscription.disabled ? '#97989b' : '#373739') : null))
                                            }
                                        />
                                        : <div className="icon"></div>
                                    }
                                </div>
                                <div className={`label ${subSubscription.disabled ? 'disabled' : ''}`}>{subSubscription.label}</div>
                                <input
                                    checked={purchasedSubscriptions[key]} disabled={!sectionEnabled || subSubscription.disabled}
                                    className={`checkbox ${!sectionEnabled || subSubscription.disabled ? 'disabled' : ''}`}
                                    disabled={!editableSubscriptions}
                                    type="checkbox"
                                    value={key}
                                    onChange={() => selectSubSubscription(subscription, key, !purchasedSubscriptions[key])}
                                />
                            </div>
                        )
                    })}
                </div>
            </div>
            {/*<div className={`content-right ${Object.keys(subscription.subSubscriptions).length > 1 ? "add-padding-top" : ''}`}>*/}
            <div className={`content-right`}>
                {subscription.editorSelect ? getEditorViewerSelect('editor', subscriptionKey, sectionEnabled): <></>}
                {subscription.viewerSelect ? getEditorViewerSelect('viewer', subscriptionKey, sectionEnabled) : <></>}
            </div>
        </div>
    }

    return (
        <div className="section-block" key={subscriptionKey}>
            {/* Header */}
            <div className="content-left">
                <div className="section-header">
                    <div className="icon-container">
                        <ReactSVG
                            className="icon"
                            src={icons[subscription.icon]}
                            beforeInjection={(svg) => fillSvgColor(svg, '#2386EE', (svgNeedsFill.includes(subscription.icon) ? '#2386EE' : null))}
                        />
                    </div>
                    <div className="title">{subscription.label}</div>
                    {/* If section is not enabled, either it hasn't been purchased or this is a new screen with no purchased products yet */}
                    <Switch
                        className="switch"
                        checked={sectionEnabled ?? false}
                        onChange={() => selectSubSubscription(subscription, subscriptionKey, !sectionEnabled)}
                        onColor="#2386EE"
                        uncheckedIcon
                        checkedIcon={false}
                        height={20}
                        width={45}
                        disabled={!editableSubscriptions}
                    />
                </div>
            </div>
            { getProductsList(subscriptionKey) }
        </div> 
    )
}

const SubscriptionBlockWithoutSubSubscriptions = ({ subscriptionKey, subscription, userOptions, location, handleLocationChange, sectionEnabled, userRoles, setUserRoles, existingUserRoles, setIsLocationEdited, changeInput, handleSelectChange, getEditorViewerSelect, editableSubscriptions }) => {

    const enableSubscription = (value) => {
        const tempPurchasedProducts = {
            ...location.purchasedProducts
        }
        tempPurchasedProducts[subscriptionKey] = value

        handleLocationChange(
            'purchasedProducts',
            tempPurchasedProducts
        );
    }

    return (
        <div className="section-block section-block--no-subsubscriptions" key={subscriptionKey}>
            {/* Header */}
            <div className="content-left">
                <div className="section-header">
                    <div className="icon-container">
                        <ReactSVG
                            className="icon"
                            src={icons[subscription.icon]}
                            beforeInjection={(svg) => fillSvgColor(svg, '#2386EE', (svgNeedsFill.includes(subscription.icon) ? '#2386EE' : null))}
                        />
                    </div>
                    <div className="title">{subscription.label}</div>
                    {/* If section is not enabled, either it hasn't been purchased or this is a new screen with no purchased products yet */}
                    <Switch
                        className="switch"
                        checked={sectionEnabled ?? false}
                        onChange={() => enableSubscription(!sectionEnabled)}
                        onColor="#2386EE"
                        uncheckedIcon
                        checkedIcon={false}
                        height={20}
                        width={45}
                        disabled={!editableSubscriptions}
                    />
                </div>
            </div>
            <div className="content-right content-right--no-subsubscriptions">
                    {subscription.viewerSelect ? getEditorViewerSelect('viewer', subscriptionKey, sectionEnabled) : <></>}
                    {subscription.editorSelect ? getEditorViewerSelect('editor', subscriptionKey, sectionEnabled): <></>}
            </div>
        </div>
    )
}

const SubscriptionBlockWithGranularPermissions = ({ subscriptionKey, subscription, userOptions, location, handleLocationChange, sectionEnabled, purchasedSubscriptions, userRoles, setUserRoles, existingUserRoles, setIsLocationEdited, handleSelectChange, editableSubscriptions }) => {

    const selectSubSubscription = (subscription, subSubscription, value) => {
        if  (!subscription.defaultSubscriptions || !(subSubscription in subscription?.defaultSubscriptions)) {

            const tempPurchasedProducts = {
                ...location.purchasedProducts
            }
            tempPurchasedProducts[subSubscription] = value;

            if (subSubscription === subscriptionKey) {
                // If the top-level switch was toggled, apply the same changes to the checkboxes.
                const defaultSubscriptions = new Set(Object.keys(subscription.defaultSubscriptions));
                const subSubscriptions = new Set();
                for (const[key, details] of Object.entries(subscription.subSubscriptions)) {
                    // If the sub-subscription is not "note", basically.
                    if ('editorSelect' in details) {
                        subSubscriptions.add(key);
                    }
                }
                const keysToToggle = [...subSubscriptions.difference(defaultSubscriptions)];
                keysToToggle.forEach((key) => {
                    tempPurchasedProducts[key] = value;
                })
            }

            handleLocationChange(
                'purchasedProducts',
                tempPurchasedProducts
            );
        }
    }

    const getEditorViewerSelect = (permission, subscriptionKey, key) => {
        // Which people
        let selectValues = [];
        if (existingUserRoles[key] && existingUserRoles[key][permission]) {
            selectValues = existingUserRoles[key][permission];
        }

        // Is the select-people box enabled?
        // Check the purchase status of contentLibrary or playlistFull (`purchasedSubscriptions[key]`)
        // before enabling or disabling the multi-select box.
        let selectDisabled = sectionEnabled ? !purchasedSubscriptions[key] : !sectionEnabled;

        return <div className="select-editor-viewer-container" style={permission === 'editor' ? {marginBottom: "0.5em"} : null}>
                <div className="label">{permission.charAt(0).toUpperCase() + permission.slice(1)}:</div>
                <div className="select-editor-viewer">
                    <Select
                        isMulti
                        // isDisabled={selectDisabled || !editableSubscriptions}
                        isDisabled={selectDisabled}
                        options={userOptions}
                        placeholder={permission.charAt(0).toUpperCase() + permission.slice(1)}
                        onChange={handleSelectChange(permission, key)}
                        value={selectValues}
                        styles={basicConfigDropdownStyling}
                    />
                </div>
            </div>
    }

    const getProductsList = (subscriptionKey) => {
        // If the Editor or Viewer multi-select box is at the sub-subscription level
        return Object.keys(subscription.subSubscriptions).map((key, idx) => {
            const subSubscription = subscription.subSubscriptions[key];
            // If we happen to have a new subscription option and the key doesn't
            // exist in the location "purchasedProducts" column, set to false
            // to prevent ReactJS from complaining about turning uncontrolled
            // inputs to controlled ones.
            if (!(key in purchasedSubscriptions)) {
                purchasedSubscriptions[key] = false;
            }

            if (subSubscription.note) {
                return <div className="products-list" key={key+idx}>
                    <div className="content-left">&nbsp;</div>
                    <div className={`content-right`}>
                        <div style={{color: (sectionEnabled ? "#ff0000" : "#777777"), paddingBottom: "1.5em"}}>{subSubscription.note}</div>
                    </div>
                </div>
            } else {
                return <div className="products-list" key={key+idx}>
                    <div className="content-left">
                        <div className="section-content">
                            <div className="row" key={idx}>
                                <div className="icon-container">
                                    {
                                        subSubscription.icon in icons
                                        ? <ReactSVG
                                            className="icon"
                                            src={icons[subSubscription.icon]}
                                            beforeInjection={(svg) => fillSvgColor(svg, '#373739', (svgNeedsFill.includes(subSubscription.icon) ? '#373739' : null))}
                                        />
                                        : <div className="icon"></div>
                                    }
                                </div>
                                <div className="label">{subSubscription.label}</div>
                                <input
                                    checked={purchasedSubscriptions[key]}
                                    className="checkbox"
                                    disabled={!editableSubscriptions || !sectionEnabled}
                                    onChange={() => selectSubSubscription(subscription, key, !purchasedSubscriptions[key])}
                                    type="checkbox"
                                    value={key}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={`content-right`}>
                        { subSubscription.editorSelect ? getEditorViewerSelect('editor', subscriptionKey, key) : <></> }
                        { subSubscription.viewerSelect ? getEditorViewerSelect('viewer', subscriptionKey, key) : <></> }
                    </div>
                </div>
            }
        })
    }

    return (
        <div className="section-block" key={subscriptionKey}>
            {/* Header */}
            <div className="content-left">
                <div className="section-header">
                    <div className="icon-container">
                        <ReactSVG
                            className="icon"
                            src={icons[subscription.icon]}
                            beforeInjection={(svg) => fillSvgColor(svg, '#2386EE', (svgNeedsFill.includes(subscription.icon) ? '#2386EE' : null))}
                        />
                    </div>
                    <div className="title">{subscription.label}</div>
                    {/* If section is not enabled, either it hasn't been purchased or this is a new screen with no purchased products yet */}
                    <Switch
                        className="switch"
                        checked={sectionEnabled ?? false}
                        onChange={() => selectSubSubscription(subscription, subscriptionKey, !sectionEnabled)}
                        onColor="#2386EE"
                        uncheckedIcon
                        checkedIcon={false}
                        height={20}
                        width={45}
                        disabled={!editableSubscriptions}
                    />
                </div>
            </div>
            { getProductsList(subscriptionKey) }
        </div>
    )
}

export default Subscriptions;
